import { useEffect, type ReactNode } from "react";
import { produce } from "immer";

import { getText } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type CircumferenceTreePropertyDescriptor,
  type SizeEstimateTreePropertyDescriptor,
  type TreeDescriptor,
} from "@/components/treeModule/lib";

import { useTreeDialogValidator } from "@/components/treeModule/treeDialog/lib";

import { RadioGroup, RadioGroupItem } from "shared/components";
import { FieldLabel } from "../FieldLabel";
import { FieldValidationMessages } from "../FieldValidationMessages";
import { SmallTreeIcon } from "./SmallTreeIcon";
import { MediumTreeIcon } from "./MediumTreeIcon";
import { LargeTreeIcon } from "./LargeTreeIcon";
import { VegetationIcon } from "./VegetationIcon";

interface SizeEstimateRadioItemProps {
  checked?: boolean;
  icon: ReactNode;
  onClick?: () => void;
  subTitle?: string;
  title: string;
  value: string;
}

function SizeEstimateRadioItem({
  checked,
  icon,
  onClick,
  subTitle,
  title,
  value,
}: SizeEstimateRadioItemProps) {
  return (
    <div
      aria-checked={checked}
      data-state={checked ? "checked" : "unchecked"}
      className="border-interactiveBorder data-[state=checked]:border-blueBorder data-[state=checked]:bg-blueBgDefault flex items-center gap-3 rounded border px-3 py-2 data-[state=checked]:border-2"
      onClick={onClick}
      role="radio"
    >
      <RadioGroupItem value={value} />

      {icon}

      <div>
        <p className="text-bodymd text-textPrimary">{title}</p>
        <p className="text-bodyxs text-subdued">{subTitle}</p>
      </div>
    </div>
  );
}

export interface SizeEstimateFieldProps {
  includeVegetationItem?: boolean;
  onChange: (treeDescriptor: TreeDescriptor) => void;
  treeDescriptor: TreeDescriptor;
}

export function SizeEstimateField<
  T extends SizeEstimateTreePropertyDescriptor,
>({ includeVegetationItem, onChange, treeDescriptor }: SizeEstimateFieldProps) {
  const { isValid, messages, shouldShowValidation } = useTreeDialogValidator<T>(
    {
      checker: (property) => !!property?.value,
      properties: treeDescriptor.properties,
      treePropertyType: TreePropertyType.size,
    },
  );

  const dbhPropertyExists = treeDescriptor.properties.has(TreePropertyType.dbh);

  const circumferencePropertyExists = treeDescriptor.properties.has(
    TreePropertyType.circumference,
  );

  const size = treeDescriptor.properties.get(TreePropertyType.size);

  useEffect(() => {
    if (size?.value) {
      const newTreeDescriptor = produce(
        treeDescriptor,
        (draftTreeDescriptor) => {
          if (dbhPropertyExists) {
            const dbh = draftTreeDescriptor?.properties.get(
              TreePropertyType.dbh,
            );
            if (dbh) {
              switch (size.value) {
                case "small":
                  dbh.value = "0.2";
                  break;
                case "medium":
                  dbh.value = "0.4";
                  break;
                case "large":
                  dbh.value = "0.7";
                  break;
              }
            }
          }
          if (circumferencePropertyExists) {
            const circumference = draftTreeDescriptor?.properties.get(
              TreePropertyType.circumference,
            ) as CircumferenceTreePropertyDescriptor;

            if (circumference) {
              switch (size.value) {
                case "small":
                  circumference.value = "0.7";
                  circumference.groundLevelCircumference = "0.7";
                  break;
                case "medium":
                  circumference.value = "1.4";
                  circumference.groundLevelCircumference = "1.6";
                  break;
                case "large":
                  circumference.value = "2.0";
                  circumference.groundLevelCircumference = "2.4";
                  break;
              }
            }
          }
        },
      );

      onChange?.(newTreeDescriptor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeDescriptor?.treeId, size?.value, onChange]);

  const handleChange = (value: string) => {
    const newTreeDescriptor = updateTreeProperty<T>(
      treeDescriptor,
      TreePropertyType.size,
      (draft) => {
        draft.value = value as T["value"];
      },
    );

    onChange?.(newTreeDescriptor);
  };

  return (
    <div className="flex flex-col gap-1">
      <FieldLabel isValid={isValid}>
        <></>
      </FieldLabel>

      <RadioGroup
        aria-invalid={!isValid && shouldShowValidation}
        className="flex flex-col gap-1.5"
        onValueChange={handleChange}
        value={size?.value}
      >
        <SizeEstimateRadioItem
          checked={size?.value === "small"}
          icon={<SmallTreeIcon />}
          onClick={() => handleChange("small")}
          subTitle={getText(
            "Less than 5 metres tall or about the height of a single storey house.",
          )}
          title={getText("Small tree")}
          value="small"
        />

        <SizeEstimateRadioItem
          checked={size?.value === "medium"}
          icon={<MediumTreeIcon />}
          onClick={() => handleChange("medium")}
          subTitle={getText(
            "5-10 metres tall or about the height of a two storey house.",
          )}
          title={getText("Medium tree")}
          value="medium"
        />

        <SizeEstimateRadioItem
          checked={size?.value === "large"}
          icon={<LargeTreeIcon />}
          onClick={() => handleChange("large")}
          subTitle={getText("10+ metres or taller than a two storey house.")}
          title={getText("Large tree")}
          value="large"
        />

        {includeVegetationItem && (
          <SizeEstimateRadioItem
            checked={size?.value === "vegetation"}
            icon={<VegetationIcon />}
            onClick={() => handleChange("vegetation")}
            subTitle={getText("E.g. shrubs, bushes.")}
            title={getText("Vegetation only")}
            value="vegetation"
          />
        )}
      </RadioGroup>

      <FieldValidationMessages messages={messages} />
    </div>
  );
}
